<template>
  <b-container fluid>
    <group-componentized-module
      v-if="!!isSensorComponentized"
      :options="sensorInformation"
      :selected-information="selectedInformation"
      @on-change-selected-information="onChangeSelectedInformationHandler"
    />
    <b-row>
      <b-col
        v-if="showManeuverTypeSelector"
        cols="12"
        md="9"
        lg="6"
        xl="4"
      >
        <b-form-group
          :label="$t('sensors.latest-maneuvers.select-maneuver-type')"
          label-for="maneuverTypeSelect"
        >
          <s-select
            v-model="selectedManeuverType"
            id="maneuverTypeSelect"
            value-field="id"
            text-field="name"
            :options="maneuverTypes"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row v-if="showEmptyMessage">
      <b-col>
        <s-empty-list :loading="loading" />
      </b-col>
    </b-row>
    <template v-else>
      <b-row>
        <b-col
          class="latest-maneuver__maneuver-data"
          cols="12"
          md=7
        >
          <div class="latest-maneuver__maneuver-data-item">
            <label>{{ $t('sensors.latest-maneuvers.maneuver') }}:</label>
            <span>{{ maneuverData.maneuverId }}</span>
          </div>
          <div class="latest-maneuver__maneuver-data-item">
            <label>{{ $t('sensors.latest-maneuvers.maneuver-date') }}:</label>
            <date-time-label :value="maneuverData.dateTime" />
          </div>
        </b-col>
        <b-col
          class="text-center text-md-right"
          cols="12"
          md="5"
        >
          <router-link
            :to="getMoreDetailsRoute()"
            target="_blank"
          >
            <b-button
              size="sm"
              variant="outline-primary"
            >
              <s-icon name="add" />
              {{ $t('actions.more-details') }}
            </b-button>
          </router-link>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          class="latest-maneuver__chart-container"
        >
          <default-chart
            class="m-2"
            legend-icon="rect"
            chart-id="sensor-maneuvers"
            first-column-i18n-title="fields.oscillography.samples"
            :chartData="chartData"
            :loading="loadingChart"
            :remove-outlier.sync="removeChartOutlier"
            :enable-multi-axis.sync="enableChartMultiAxis"
            :start-index-from-one="true"
            @toggle-outlier="refreshChartData(true)"
            @toggle-multi-axis="refreshChartData(true)"
          />
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import DateTimeLabel from '@/components/globals/DateTimeLabel';
import loadingMixin from '@/services/loadingMixin';
import LatestManeuversService from '@/domain/services/installation/supervision/sensors/latestManeuvers';
import AssetService from '@/domain/services/asset';
import eventBus from '@/services/globalEventBus';
import event from '@/domain/events/engineeringModulesHeader';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import DefaultChart from '@/components/charts/DefaultChart';
import { ModuleCategorys } from 'sigma-mappings';
import GroupComponentizedModule from '@/domain/components/installation/supervision/sensors/GroupComponentizedModule';

export default {
  props: {
    sensor: {
      type: Object,
    },
    asset: {
      type: Object,
    },
    isSensorComponentized: {
      type: Boolean,
      default: false,
    },
    category: String,
    selectedInformation: {
      type: Object,
      default: () => ({}),
    },
    sensorInformation: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    DateTimeLabel,
    DefaultChart,
    GroupComponentizedModule,
  },
  mixins: [
    loadingMixin,
    defaultChartMixin,
  ],
  data: () => ({
    maneuverData: null,
    maneuverTypes: null,
    selectedManeuverType: null,
  }),
  computed: {
    chartData() {
      return (this.maneuverData || {}).chartData || [];
    },
    showEmptyMessage() {
      return this.loading
        || !this.chartData.length;
    },
    showManeuverTypeSelector() {
      return this.maneuverTypes && this.maneuverTypes.length > 1;
    },
    selectedAssetModuleId() {
      const { sensorId } = this.$route.params;
      const { assetModuleId } = this.selectedInformation || {};
      return this.category === ModuleCategorys.COMPONENTIZED
        ? assetModuleId
        : sensorId;
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    init() {
      this.waitLoading(this.refreshManeuverTypes());
      eventBus.on(event.refresh, this.refreshDataEvent, this);
    },
    deinit() {
      eventBus.off(event.refresh, this.refreshDataEvent, this);
    },
    refreshDataEvent({ wait }) {
      wait(this.refreshChartData());
    },
    async refreshManeuverTypes() {
      if (this.asset.assetTypeId) {
        this.maneuverTypes = (await AssetService.getManeuverTypes(this.asset.assetTypeId));
        this.selectedManeuverType = this.maneuverTypes[0]
        ? this.maneuverTypes[0].id
        : null;
      }
    },
    refreshChartData(loadingChart) {
      if (!this.selectedManeuverType) {
        return;
      }
      if (!loadingChart) {
        this.startLoading();
      }
      this.loadingChart = loadingChart;
      LatestManeuversService.getManeuverData(
        this.selectedAssetModuleId,
        this.selectedManeuverType,
        this.chartFilters,
      ).then((data) => {
        this.maneuverData = data
          ? { ...data.maneuvers[0], chartData: data.chartData }
          : null;
      }).finally(() => {
        this.endLoading();
        this.loadingChart = false;
      });
    },
    getMoreDetailsRoute() {
      return {
        name: 'clean-view.sensors.latest-maneuvers-more-details',
        params: {
          sensorId: this.selectedAssetModuleId,
          maneuverType: this.selectedManeuverType,
        },
      };
    },
    onChangeSelectedInformationHandler(value) {
      this.$emit('on-change-selected-information', value);
    },
  },
  watch: {
    selectedManeuverType() {
      this.refreshChartData();
    },
    asset() {
      this.refreshManeuverTypes();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.latest-maneuver
  &__maneuver-data
    padding-top: 1em
  &__maneuver-data-item
    +media-min-md
      display: inline-block
      white-space: nowrap
      &:not(:last-child)
        margin-right: 1em
  +media-max-sm
    &__chart-container
      overflow-y: auto
    &__chart
      min-width: 700px
</style>
